import React, {
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import {
  Staff,
  StoreLocation,
  User,
  UserToBusinessEntity,
  UserToStoreLocation,
} from "./models";
import { AuthUser } from "aws-amplify/auth";
import {
  getBusinessEntityIDOfUser,
  getStaffsForBusinessEntity,
  getStoreLocationsForBusinessEntity,
  getStoreLocationsForUser,
  getUser,
} from "./api";

interface IUserContext {
  authUser: AuthUser | null;
  user: User | null;
  businessEntityID: string | null;
  storeLocations: StoreLocation[];
  staffs: Staff[];
  refresh: boolean;
  setRefresh: Dispatch<SetStateAction<boolean>>;
}

interface Props {
  children: JSX.Element | Array<JSX.Element>;
  user: AuthUser;
}

const defaultContext: IUserContext = {
  authUser: null,
  user: null,
  businessEntityID: null,
  storeLocations: [],
  staffs: [],
  refresh: false,
  setRefresh: () => {},
};

const UserContext = createContext(defaultContext);

const UserContextProvider = (props: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [businessEntityID, setBusinessEntityID] = useState<string | null>(null);
  const [storeLocations, setStoreLocations] = useState<StoreLocation[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    getUser(props.user.userId).then((newUser: User) => {
      setUser(newUser);
      getBusinessEntityIDOfUser(props.user.userId).then(
        (userToBusinessEntity: UserToBusinessEntity) => {
          const businessEntityID = userToBusinessEntity.businessEntityId;
          getStoreLocationsForUser(props.user.userId).then(
            (userStoreLocations: UserToStoreLocation[]) => {
              getStoreLocationsForBusinessEntity(businessEntityID).then(
                (storeLocations: StoreLocation[]) => {
                  getStaffsForBusinessEntity(businessEntityID).then(
                    (staffs: Staff[]) => {
                      setBusinessEntityID(businessEntityID);
                      setStoreLocations(
                        storeLocations.filter((storeLocation: StoreLocation) =>
                          userStoreLocations
                            .map((l) => l.storeLocationId)
                            .includes(storeLocation.id)
                        )
                      );
                      setStaffs(staffs);
                    }
                  );
                }
              );
            }
          );
        }
      );
    });
  }, []);

  useEffect(() => {
    getUser(props.user.userId).then((user: User) => {
      getBusinessEntityIDOfUser(props.user.userId).then(
        (userToBusinessEntity: UserToBusinessEntity) => {
          const businessEntityID = userToBusinessEntity.businessEntityId;
          getStoreLocationsForUser(props.user.userId).then(
            (userStoreLocations: UserToStoreLocation[]) => {
              getStoreLocationsForBusinessEntity(businessEntityID).then(
                (storeLocations: StoreLocation[]) => {
                  getStaffsForBusinessEntity(businessEntityID).then(
                    (staffs: Staff[]) => {
                      setUser(user);
                      setBusinessEntityID(businessEntityID);
                      setStoreLocations(
                        storeLocations.filter((storeLocation: StoreLocation) =>
                          userStoreLocations
                            .map((l) => l.storeLocationId)
                            .includes(storeLocation.id)
                        )
                      );
                      setStaffs(staffs);
                    }
                  );
                }
              );
            }
          );
        }
      );
    });
  }, [refresh]);

  return (
    <UserContext.Provider
      value={{
        user: user,
        authUser: props.user,
        businessEntityID: businessEntityID,
        storeLocations: storeLocations,
        staffs: staffs,
        refresh: refresh,
        setRefresh: setRefresh,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export { UserContextProvider, UserContext };
export type { IUserContext };
